/**
 * Роли команды Центрики (team-access): подписи, право назначать менеджеров клиентам и
 * ответственных в заказах и видимость данных. Зеркало бэкенда — `domain.Role` (`IsTeam`,
 * `CanAssign`, `SeesAllClients`).
 */
import { useAuth } from '@/app/auth-context'
import type { Role, TeamRole } from '@/lib/api'

export const TEAM_ROLE_LABEL: Record<TeamRole, string> = {
  admin: 'Администратор',
  head: 'Руководитель',
  employee: 'Сотрудник',
}

/** Порядок в выпадающем списке роли: от самой ограниченной к самой широкой. */
export const TEAM_ROLES: TeamRole[] = ['employee', 'head', 'admin']

/** Менеджеров клиентам и ответственных в заказах назначают руководитель и администратор. */
export function canAssign(role: Role | undefined): boolean {
  return role === 'admin' || role === 'head'
}

/**
 * Срок договора абонентского обслуживания по обучению в реквизитах клиента задают администратор
 * и руководитель (training-subscription-head); сотрудник видит срок без правки. Отдельно от
 * `canAssign`: совпадение ролей сегодня не делает это «назначением». Зеркало бэкенда — группа
 * маршрутов `requireRole(admin, head)` для `PUT /team/clients/{cid}/training-subscription`.
 */
export function canEditTrainingSubscription(role: Role | undefined): boolean {
  return role === 'admin' || role === 'head'
}

/**
 * Роль видит данные всех клиентов (team-visibility): администратор и руководитель. Сотрудник
 * видит только закреплённое за ним — клиентов, у которых он менеджер, и заказы, где он
 * ответственный. Ограничение применяет сервер; здесь — только подписи страниц.
 */
export function seesAllClients(role: Role | undefined): boolean {
  return role === 'admin' || role === 'head'
}

/** Вошедший видит данные всех клиентов (для подписей командных страниц). */
export function useSeesAllClients(): boolean {
  const { context } = useAuth()
  return seesAllClients(context?.user.role)
}
